// react
import { useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import type { FieldError } from 'react-hook-form';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import LocalOfferIcon from '@mui/icons-material/LocalOffer';

// material ui
import {
  Autocomplete,
  Box,
  Card,
  CardContent,
  Chip,
  FormHelperText,
  Stack,
  TextField,
} from '@mui/material';

// api
import { useTagsGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/tags';
import {
  useUnitsPostAsyncByunitMutation,
  useUnitsPostTagAsyncByidnewTagMutation,
} from '@/api/generated/recalc/endpoints/v1/units.enhanced';
import type { UnitsPostAsyncByunitEnhancedResponse } from '@/api/generated/recalc/endpoints/v1/units.enhanced';
import { useUnitTypesGetCollectionAsyncByqueryOptionsQuery } from '@/api/generated/recalc/endpoints/v1/unitTypes';

// shared components
import { CancelButton, SubmitButton } from '@/components/ui/button';
import {
  RHFAddressAutocomplete,
  RHFFieldLabel,
  RHFTextField,
} from '@/components/ui/form';

// relative
import {
  buildInitialUnitTypeInputs,
  toCreateUnitFormValues,
  toPostUnitPayload,
} from './createUnitForm.mappers';
import { createUnitFormSchema } from './createUnitForm.schema';
import type { SelectedUnitType, UnitTag } from './createUnitForm.schema';
import UnitTypeInputsSection from './UnitTypeInputsSection';
import type {
  UnitTypeInputsGetValues,
  UnitTypeInputsSetValue,
} from './UnitTypeInputsSection';

import { zodResolver } from '@hookform/resolvers/zod';

export default function CreateUnitForm({
  accountId,
  onSuccess,
  onCancel,
}: {
  accountId: number;
  onSuccess: (unitName: string) => void;
  onCancel: () => void;
}) {
  const { t } = useTranslation([
    'forms',
    'validation',
    'units-gateways-sensors',
  ]);

  const { data: unitTypes, isLoading: isLoadingUnitTypes } =
    useUnitTypesGetCollectionAsyncByqueryOptionsQuery({});

  const { data: tags } = useTagsGetCollectionAsyncByqueryOptionsQuery(
    { 'Account.AccountId': `eq(${accountId})` },
    { skip: !accountId },
  );
  const [postUnit, { isLoading: isCreating }] =
    useUnitsPostAsyncByunitMutation();

  const [postUnitTag] = useUnitsPostTagAsyncByidnewTagMutation();

  const tagList = (tags as UnitTag[] | undefined) ?? [];
  const unitTypeOptions = (unitTypes as SelectedUnitType[] | undefined) ?? [];
  const [tagInputValue, setTagInputValue] = useState('');

  const schema = createUnitFormSchema(t);
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: toCreateUnitFormValues(),
    mode: 'onTouched',
  });

  const unitType = useWatch({ control, name: 'unitType' });

  const onSubmit = handleSubmit(async (values) => {
    const createUnitDto = toPostUnitPayload(values, accountId);
    const unitResult = await postUnit({ createUnitDto });

    if (!('error' in unitResult)) {
      const { unitId } =
        unitResult.data as UnitsPostAsyncByunitEnhancedResponse;

      if (unitId && values.tags.length > 0) {
        await Promise.all(
          values.tags.map((tagText) =>
            postUnitTag({
              id: parseInt(unitId, 10),
              createTagDto: { Text: tagText },
            }),
          ),
        );
        // Tag errors handled by apiErrorMiddleware (snackbar + App Insights).
      }

      onSuccess(values.name.trim());
    }
    // Error handled by apiErrorMiddleware (snackbar + App Insights).
  });

  return (
    <form onSubmit={onSubmit} noValidate data-testid="unit-create-form">
      <Stack spacing={2}>
        <Card variant="outlined" data-testid="unit-create-form-card">
          <CardContent>
            <Stack spacing={2}>
              <RHFTextField
                name="name"
                control={control}
                required
                fullWidth
                disabled={isCreating}
                label={t('createUnit.name.label', { ns: 'forms' })}
                placeholder={t('createUnit.name.placeholder', {
                  ns: 'forms',
                })}
                helperText={t('createUnit.name.helperText', {
                  ns: 'forms',
                })}
                data-testid="unit-create-name-input"
              />

              <RHFAddressAutocomplete
                name="address"
                control={control}
                disabled={isCreating}
                required
                fullWidth
                size="small"
                label={t('createUnit.address.label', { ns: 'forms' })}
                placeholder={t('createUnit.address.placeholder', {
                  ns: 'forms',
                })}
                helperText={t('createUnit.address.helperText', {
                  ns: 'forms',
                })}
              />

              <Controller
                name="tags"
                control={control}
                render={({ field }) => (
                  <Box>
                    <RHFFieldLabel>
                      {t('createUnit.tags.label', { ns: 'forms' })}
                    </RHFFieldLabel>
                    <Autocomplete<string, true, false, true>
                      freeSolo
                      multiple
                      disabled={isCreating}
                      options={[...new Set(tagList.map((tag) => tag.Text))]}
                      value={field.value}
                      inputValue={tagInputValue}
                      onInputChange={(_event, newInputValue) => {
                        setTagInputValue(newInputValue);
                      }}
                      onChange={(_event, newValue) => {
                        field.onChange(newValue);
                        setTagInputValue('');
                      }}
                      onBlur={() => {
                        const trimmedValue = tagInputValue.trim();
                        if (
                          trimmedValue &&
                          !field.value.includes(trimmedValue)
                        ) {
                          field.onChange([...field.value, trimmedValue]);
                        }
                        setTagInputValue('');
                        field.onBlur();
                      }}
                      renderValue={(value, getItemProps) =>
                        value.map((option, index) => {
                          const { key, ...tagProps } = getItemProps({
                            index,
                          });
                          return (
                            <Chip
                              key={key}
                              icon={
                                <LocalOfferIcon
                                  sx={{ color: 'white !important' }}
                                />
                              }
                              label={option}
                              sx={{
                                borderRadius: 1,
                                backgroundColor: 'info.main',
                                color: 'white',
                                '& .MuiChip-label': {
                                  color: 'white',
                                },
                              }}
                              size="small"
                              {...tagProps}
                            />
                          );
                        })
                      }
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder={t('createUnit.tags.placeholder', {
                            ns: 'forms',
                          })}
                          helperText={t('createUnit.tags.helperText', {
                            ns: 'forms',
                          })}
                          size="small"
                        />
                      )}
                    />
                  </Box>
                )}
              />

              <RHFTextField
                name="description"
                control={control}
                fullWidth
                multiline
                minRows={2}
                disabled={isCreating}
                label={t('createUnit.description.label', { ns: 'forms' })}
                placeholder={t('createUnit.description.placeholder', {
                  ns: 'forms',
                })}
                helperText={t('createUnit.description.helperText', {
                  ns: 'forms',
                })}
              />
              <Controller
                name="unitType"
                control={control}
                render={({ field, fieldState }) => (
                  <Box>
                    <RHFFieldLabel required>
                      {t('createUnit.unitType.label', { ns: 'forms' })}
                    </RHFFieldLabel>
                    <Autocomplete<SelectedUnitType, false, false, false>
                      options={unitTypeOptions}
                      getOptionLabel={(option) => option.Name}
                      value={field.value}
                      loading={isLoadingUnitTypes}
                      disabled={isCreating}
                      onChange={(_event, newValue) => {
                        field.onChange(newValue);
                        setValue(
                          'unitTypeInputs',
                          buildInitialUnitTypeInputs(newValue),
                          { shouldValidate: true },
                        );
                      }}
                      onBlur={field.onBlur}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          data-testid="unit-create-unit-type-input"
                          required
                          fullWidth
                          size="small"
                          error={!!fieldState.error}
                          helperText={
                            fieldState.error?.message ??
                            t('createUnit.unitType.helperText', {
                              ns: 'forms',
                            })
                          }
                          placeholder={t('createUnit.unitType.placeholder', {
                            ns: 'forms',
                          })}
                        />
                      )}
                    />
                  </Box>
                )}
              />

              {unitType && (
                <Stack spacing={1}>
                  <UnitTypeInputsSection
                    key={unitType.UnitTypeId}
                    control={control}
                    getValues={getValues as UnitTypeInputsGetValues}
                    setValue={setValue as unknown as UnitTypeInputsSetValue}
                    selectedUnitType={unitType}
                    recordedDataTypes={unitType.RecordedDataTypes ?? []}
                  />
                  {(errors.unitTypeInputs as FieldError | undefined)
                    ?.message ? (
                    <FormHelperText error>
                      {
                        (errors.unitTypeInputs as FieldError | undefined)
                          ?.message
                      }
                    </FormHelperText>
                  ) : null}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>

        <Box display="flex" justifyContent="flex-end" gap={1}>
          <CancelButton
            onClick={onCancel}
            disabled={isCreating}
            data-testid="unit-create-cancel-button"
          >
            {t('common.cancel', { ns: 'forms' })}
          </CancelButton>
          <SubmitButton
            disabled={isCreating}
            data-testid="unit-create-submit-button"
          >
            {isCreating
              ? t('createUnit.submitting', { ns: 'forms' })
              : t('createUnit.submit', { ns: 'forms' })}
          </SubmitButton>
        </Box>
      </Stack>
    </form>
  );
}
